import { AxiosError } from 'axios';

import { ExperimentId } from '../../common/experiments/experimentsConstants';
import { ApplicationType } from '../../common/platform/platformTypes';
import { isFeatureEnabled } from '../../common/users/utils/userPropertyUtils';
import { METHODS } from '../../common/utils/http/httpConstants';
import getClientConfig from '../utils/getClientConfig';
import platformSingleton from '../platform/platformSingleton';
import { isPlatformModernMobileApp } from '../platform/utils/platformDetailsUtils';
import { SERVER_TYPE } from '../../../config/configConstants';
import {
    removeCurrentUserToken,
    getCurrentUserToken,
    setCurrentUserToken,
    removeRememberedBetterAuthSessionUser,
    setRememberedBetterAuthSessionUser,
    type BetterAuthSessionUser,
} from './authService';
import { http } from '../utils/services/http';

export type BetterAuthSessionMigrationStatus =
    | 'idle'
    | 'upgrading'
    | 'downgrading'
    | 'upgraded'
    | 'downgraded'
    | 'failed';

let migrationStatus: BetterAuthSessionMigrationStatus = 'idle';

export const getBetterAuthSessionMigrationStatus = (): BetterAuthSessionMigrationStatus => migrationStatus;

export const resetBetterAuthSessionMigrationStatus = (): void => {
    migrationStatus = 'idle';
};

const isDevelopmentServerEnvironment = (): boolean =>
    getClientConfig().serverType === SERVER_TYPE.development;

const isBetterAuthSessionMigrationPlatform = (): boolean => {
    if (platformSingleton.appType === ApplicationType.web) return true;

    // Native (Capacitor) migration depends on a Secure, Domain=.milanote.com session cookie in the
    // native store, which the WebView only sends to https *.milanote.com hosts. A local dev build
    // talks to a localhost backend where that cookie can never be delivered, so upgrading there would
    // clear the legacy JWT and lock the user out. Restrict native migration to deployed environments
    // (staging/production); web is unaffected.
    if (isPlatformModernMobileApp(platformSingleton)) return !isDevelopmentServerEnvironment();

    return false;
};

export const shouldUpgradeLegacySessionToBetterAuth = (user: object | null | undefined): boolean => {
    if (!user) return false;
    if (!isBetterAuthSessionMigrationPlatform()) return false;
    if (!getCurrentUserToken()) return false;
    if (!isFeatureEnabled(ExperimentId.betterAuth, user)) return false;

    return migrationStatus === 'idle';
};

export const shouldDowngradeBetterAuthSessionToLegacy = (user: object | null | undefined): boolean => {
    if (!user) return false;
    if (!isBetterAuthSessionMigrationPlatform()) return false;
    if (getCurrentUserToken()) return false;
    if (isFeatureEnabled(ExperimentId.betterAuth, user)) return false;

    return migrationStatus === 'idle';
};

const verifyBetterAuthSession = async (): Promise<BetterAuthSessionUser | null> => {
    const response = await http({
        url: 'users/me',
        withCredentials: true,
        skipAuthHeader: true,
        retry: 0,
    });

    const user = response.data?.user;
    if (!user) return null;
    if (typeof user !== 'object') throw new Error('Verified Better Auth session returned invalid user data');

    return user as BetterAuthSessionUser;
};

export const upgradeLegacySessionToBetterAuthIfFlagged = async (user: object | null | undefined): Promise<boolean> => {
    if (!shouldUpgradeLegacySessionToBetterAuth(user)) return false;

    migrationStatus = 'upgrading';

    try {
        await http({
            method: METHODS.POST,
            url: 'auth/upgrade-better-auth-session',
            withCredentials: true,
        });

        // The upgrade response sets the session cookie via `Set-Cookie`, which the WebView stores;
        // verify the cookie-only /users/me request authenticates before dropping the legacy token.
        const verifiedUser = await verifyBetterAuthSession();
        setRememberedBetterAuthSessionUser(verifiedUser || user);
        removeCurrentUserToken();

        migrationStatus = 'upgraded';
        return true;
    } catch (error) {
        migrationStatus = 'failed';
        throw error as AxiosError;
    }
};

export const downgradeBetterAuthSessionToLegacyIfUnflagged = async (
    user: object | null | undefined,
): Promise<boolean> => {
    if (!shouldDowngradeBetterAuthSessionToLegacy(user)) return false;

    migrationStatus = 'downgrading';

    try {
        const response = await http({
            method: METHODS.POST,
            url: 'auth/downgrade-better-auth-session',
            withCredentials: true,
            skipAuthHeader: true,
        });

        const token = response.data?.token;
        if (typeof token !== 'string') throw new Error('Downgraded Better Auth session did not return a legacy token');

        setCurrentUserToken(token);
        removeRememberedBetterAuthSessionUser();

        migrationStatus = 'downgraded';
        return true;
    } catch (error) {
        migrationStatus = 'failed';
        throw error as AxiosError;
    }
};
